import {
  SafetyOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  Table, Button, message, Modal, Space, Form, Input, Tag, Checkbox, Divider, Typography, Collapse,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import usePermissions from "../../../hooks/usePermissions";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import { fixedStart } from "../../../i18";

const { Text } = Typography;

// Only show permissions that map to sidebar screens / real actions.
const HIDDEN_PERMISSION_MODULES = new Set([
  "stock",
  "purchase_orders",
  "units",
  "categories",
  "dashboard", // POS is the home screen; old dashboard.view is unused
]);

const HIDDEN_PERMISSION_NAMES = new Set([
  "reports.payables_receivables", // not in sidebar
  "data.products.all", // products are shared, not scoped
  // Old cart/shop — not used; sales are made in POS
  "orders.add",
  "orders.edit",
  "orders.delete",
  // Replaced by separate Add + Cancel permissions
  "vendors.expenses",
  "company_expenses.manage",
]);

/** Clear sidebar-style group titles for Roles UI */
const MODULE_LABELS = {
  data: "Data",
  pos: "POS",
  orders: "Sales History",
  reports: "Reports & Accounting",
  customers: "Customers",
  vendors: "Farmers",
  vendor_expenses: "Farmer Expenses",
  vendor_expense_types: "Farmer Expense Types",
  company_expenses: "Company Expenses",
  company_expense_types: "Company Expense Types",
  products: "Products",
  users: "Users",
  roles: "Roles",
  maintenance: "System",
};

/** Split crowded modules into clearer Roles UI groups (DB module stays the same). */
const uiGroupKey = (p) => {
  const name = p?.name || "";
  const mod = p?.module || "other";
  if (mod === "vendors") {
    if (name.includes("expense_types")) return "vendor_expense_types";
    if (
      name.includes("expense")
      || name === "vendors.view_expenses"
      || name === "vendors.expenses"
      || name === "vendors.expenses_add"
      || name === "vendors.expenses_cancel"
    ) {
      return "vendor_expenses";
    }
    return "vendors";
  }
  if (mod === "company_expenses") {
    if (name.includes("expense_types")) return "company_expense_types";
    return "company_expenses";
  }
  return mod;
};

const isActiveUiPermission = (p) => {
  if (!p) return false;
  if (HIDDEN_PERMISSION_MODULES.has(p.module)) return false;
  if (HIDDEN_PERMISSION_NAMES.has(p.name)) return false;
  return true;
};

const GetRoles = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const [roleForm] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  /** Hidden (unused) perms already on the role — kept on save, not shown in UI */
  const [lockedHiddenIds, setLockedHiddenIds] = useState([]);
  const [permSaving, setPermSaving] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRoleData, setEditingRoleData] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/roles");
      setRoles(res.data.roles || []);
    } catch { setRoles([]); }
    finally { setLoading(false); }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await apiClient.get("/permissions");
      setAllPermissions(res.data.permissions || []);
    } catch { setAllPermissions([]); }
  }, []);

  useEffect(() => { fetchRoles(); fetchPermissions(); }, [fetchRoles, fetchPermissions]);

  const uiPermissions = useMemo(
    () => allPermissions.filter(isActiveUiPermission),
    [allPermissions]
  );
  const hiddenPermissionIds = useMemo(
    () => new Set(allPermissions.filter((p) => !isActiveUiPermission(p)).map((p) => p.id)),
    [allPermissions]
  );

  const openCreateModal = () => { setEditingRoleData(null); roleForm.resetFields(); setRoleModalOpen(true); };
  const openEditModal = (role) => { setEditingRoleData(role); roleForm.setFieldsValue({ name: role.name, display_name: role.display_name }); setRoleModalOpen(true); };

  const handleSaveRole = async () => {
    try {
      const values = await roleForm.validateFields();
      setRoleSaving(true);
      const payload = { name: values.name.toLowerCase().replace(/\s+/g, "_"), display_name: values.display_name };
      if (editingRoleData) { await apiClient.put(`/roles/${editingRoleData.id}`, payload); message.success(t("roleUpdated")); }
      else { await apiClient.post("/roles", payload); message.success(t("roleCreated")); }
      setRoleModalOpen(false); setEditingRoleData(null); roleForm.resetFields(); fetchRoles();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(t("roleSaveError"));
    } finally { setRoleSaving(false); }
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/roles/${deleteTarget.id}`);
      message.success(t("roleDeleted"));
      setDeleteTarget(null);
      fetchRoles();
    } catch { message.error(t("roleDeleteError")); }
  };

  const openPermModal = (role) => {
    setEditingRole(role);
    const assigned = (role.permissions || []).map((p) => p.id);
    setLockedHiddenIds(assigned.filter((id) => hiddenPermissionIds.has(id)));
    setSelectedPermIds(assigned.filter((id) => !hiddenPermissionIds.has(id)));
    setPermModalOpen(true);
  };

  const handlePermSave = async () => {
    if (!editingRole) return;
    try {
      setPermSaving(true);
      // Keep any unused/hidden perms already on the role so we don't strip them by accident
      const permissions = [...new Set([...selectedPermIds, ...lockedHiddenIds])];
      await apiClient.put(`/roles/${editingRole.id}/permissions`, { permissions });
      message.success(t("permissionsUpdated"));
      setPermModalOpen(false);
      fetchRoles();
    } catch { message.error(t("permissionsUpdateError")); }
    finally { setPermSaving(false); }
  };

  const togglePermission = (id) =>
    setSelectedPermIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleModule = (modulePerms) => {
    const ids = modulePerms.map((p) => p.id);
    const allSelected = ids.every((id) => selectedPermIds.includes(id));
    setSelectedPermIds((prev) => allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]);
  };

  const selectAll = () => setSelectedPermIds(uiPermissions.map((p) => p.id));
  const deselectAll = () => setSelectedPermIds([]);

  const featurePerms = useMemo(() => uiPermissions.filter((p) => p.type === "feature"), [uiPermissions]);
  const dataPerms = useMemo(() => uiPermissions.filter((p) => p.type === "data"), [uiPermissions]);

  const groupByModule = (perms) => {
    const groups = {};
    perms.forEach((p) => {
      const mod = uiGroupKey(p);
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(p);
    });
    // Sidebar-ish order so Roles UI is easy to scan
    const order = [
      "pos", "orders", "reports", "customers",
      "vendors", "vendor_expenses", "vendor_expense_types",
      "company_expenses", "company_expense_types",
      "products", "users", "roles", "maintenance", "other",
    ];
    const ordered = {};
    order.forEach((key) => {
      if (groups[key]) ordered[key] = groups[key];
    });
    Object.keys(groups).forEach((key) => {
      if (!ordered[key]) ordered[key] = groups[key];
    });
    return ordered;
  };

  const featureGroups = useMemo(() => groupByModule(featurePerms), [featurePerms]);
  // Data-visibility permissions all live in one flat "Data" group — no per-module
  // sub-sections — so admins see every all-vs-own toggle in a single place.
  const dataGroups = useMemo(() => (dataPerms.length ? { data: dataPerms } : {}), [dataPerms]);

  const renderPermissionGroup = (groups, title) => (
    <>
      <Text strong style={{ fontSize: 15, display: "block", marginBottom: 8 }}>{title}</Text>
      <Collapse accordion={false} defaultActiveKey={Object.keys(groups)} items={Object.entries(groups).map(([module, perms]) => {
        const ids = perms.map((p) => p.id);
        const allChecked = ids.every((id) => selectedPermIds.includes(id));
        const someChecked = ids.some((id) => selectedPermIds.includes(id));
        const moduleLabel = MODULE_LABELS[module]
          || (module === "other" ? t("roles.otherModule", "Other") : module);
        return {
          key: module,
          label: (
            <Space>
              <Checkbox checked={allChecked} indeterminate={someChecked && !allChecked} onChange={() => toggleModule(perms)} onClick={(e) => e.stopPropagation()} />
              <span style={{ fontWeight: 600 }}>{moduleLabel}</span>
              <Tag style={{ borderRadius: 12, marginInlineStart: 4 }}>{perms.length}</Tag>
            </Space>
          ),
          children: (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {perms.map((p) => (
                <label
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: selectedPermIds.includes(p.id) ? "#eff6ff" : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={selectedPermIds.includes(p.id)}
                    onChange={() => togglePermission(p.id)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 600, color: T.text }}>{p.display_name}</span>
                    {(p.description || p.name) && (
                      <span style={{ display: "block", fontSize: 12, color: T.muted, marginTop: 2 }}>
                        {p.description || p.name}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          ),
        };
      })} />
    </>
  );

  const columns = [
    {
      title: <b>{t("actions")}</b>,
      key: "actions",
      fixed: fixedStart(i18n.language),
      render: (_, record) => (
        <Space>
          {hasPermission("roles.edit") && (
            <Button type="primary" size="small" icon={<SafetyOutlined />} onClick={() => openPermModal(record)} style={{ borderRadius: 7 }}>
              {t("managePermissions")}
            </Button>
          )}
          {hasPermission("roles.edit") && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} style={{ borderRadius: 7 }} />
          )}
          {hasPermission("roles.delete") && record.name !== "admin" && (
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => setDeleteTarget(record)} style={{ borderRadius: 7 }} />
          )}
        </Space>
      ),
    },
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => <span style={{ color: T.muted, fontSize: 13 }}>{index + 1}</span>,
    },
    {
      title: <b>{t("roleDisplayName")}</b>,
      dataIndex: "display_name",
      key: "display_name",
      render: (text) => <span style={{ fontWeight: 600, color: T.text }}>{text}</span>,
    },
    {
      title: <b>{t("roleName")}</b>,
      dataIndex: "name",
      key: "name",
      render: (name) => <Tag style={{ borderRadius: 20 }}>{name}</Tag>,
    },
    {
      title: <b>{t("permissionsCount")}</b>,
      key: "permCount",
      render: (_, record) => {
        const visibleCount = (record.permissions || []).filter((p) => isActiveUiPermission(p)).length;
        return (
          <span style={{ background: "#dbeafe", color: T.primary, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
            {visibleCount}
          </span>
        );
      },
    },
  ];

  const stats = [
    <StatCard key="total" icon={<SafetyCertificateOutlined style={{ color: "#2563eb" }} />} label={t("rolesAndPermissions")} value={roles.length} iconBg="#dbeafe" />,
    <StatCard key="perms" icon={<SafetyOutlined style={{ color: "#7c3aed" }} />} label={t("roles.permissions", { defaultValue: "Permissions" })} value={uiPermissions.length} iconBg="#ede9fe" />,
  ];

  const toolbar = hasPermission("roles.add") ? (
    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} style={{ borderRadius: 9, fontWeight: 600 }}>
      {t("addRole")}
    </Button>
  ) : null;

  return (
    <>
      <ListPage
        breadcrumb={[t("navigation.administration", { defaultValue: "Administration" }), t("rolesAndPermissions")]}
        title={t("rolesAndPermissions")}
        subtitle={t("roles.count", { defaultValue: "{{count}} roles", count: roles.length })}
        stats={stats}
        toolbar={toolbar}
      >
        <Table
          loading={loading}
          dataSource={roles.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          pagination={false}
          scroll={{ x: 700 }}
        />
      </ListPage>

      <Modal title={editingRoleData ? t("editRole") : t("addRole")} open={roleModalOpen} onOk={handleSaveRole} confirmLoading={roleSaving} onCancel={() => { setRoleModalOpen(false); setEditingRoleData(null); roleForm.resetFields(); }} okText={t("save")} cancelText={t("cancel")} centered>
        <Form form={roleForm} layout="vertical">
          <Form.Item name="name" label={t("roleName")} rules={[{ required: true, message: t("enterRoleName") }]}>
            <Input placeholder={t("roles.namePlaceholder", { defaultValue: "e.g. accountant" })} disabled={editingRoleData?.name === "admin"} />
          </Form.Item>
          <Form.Item name="display_name" label={t("roleDisplayName")} rules={[{ required: true, message: t("enterRoleDisplayName") }]}>
            <Input placeholder={t("roles.displayNamePlaceholder", { defaultValue: "e.g. Accountant" })} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`${t("managePermissions")} - ${editingRole?.display_name || ""}`} open={permModalOpen} onOk={handlePermSave} confirmLoading={permSaving} onCancel={() => setPermModalOpen(false)} okText={t("save")} cancelText={t("cancel")} centered width={720}>
        <Space style={{ marginBottom: 12 }}>
          <Button size="small" onClick={selectAll}>{t("selectAll")}</Button>
          <Button size="small" onClick={deselectAll}>{t("deselectAll")}</Button>
        </Space>
        {Object.keys(featureGroups).length > 0 && <>{renderPermissionGroup(featureGroups, t("featurePermissions"))}<Divider /></>}
        {Object.keys(dataGroups).length > 0 && renderPermissionGroup(dataGroups, t("dataVisibility"))}
      </Modal>

      <Modal title={t("confirmDelete")} open={Boolean(deleteTarget)} onOk={handleDeleteRole} onCancel={() => setDeleteTarget(null)} okText={t("yesDelete")} okButtonProps={{ danger: true }} cancelText={t("cancel")} centered>
        <p>{t("deleteRoleConfirm")}</p>
      </Modal>
    </>
  );
};

export default GetRoles;
